export async function postClient(clientParameters = {}) {
  // Trim the client name if it exists to prevent trailing spaces
  const cleanedParameters = {
    ...clientParameters,
    name: clientParameters.name ? clientParameters.name.trim() : clientParameters.name
  };

  // Construct the payload with spread syntax
  const clientPayload = {
    client_data: [
      {
        ...cleanedParameters, // Spread all properties from cleanedParameters
        user_create: cleanedParameters.user_create || "system_default", // Default value
        user_update: cleanedParameters.user_update || "system_default", // Default value
      },
    ],
  };

  try {
    // Make the API call
    const response = await fetch(
      "https://ghyv9t95ih.execute-api.ap-southeast-1.amazonaws.com/prod/postClient",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(clientPayload),
      }
    );

    // Check for a successful response
    if (!response.ok) {
      const errorResponse = await response.json();
      console.error(
        `Error: Failed to post client data. Status: ${response.status}, Message: ${errorResponse.message}`
      );
      throw new Error(
        `Failed to post client data. Status: ${response.status}, Message: ${errorResponse.message}`
      );
    } else {
      return response; // Return the response object if successful
    }
  } catch (error) {
    // Catch any network or other errors
    console.error(
      "Error: Network or other error occurred while posting client data.",
      error
    );
    throw error; // Re-throw the error
  }
}

export async function getClients() {
  try {
    // Make the API call
    const response = await fetch(
      "https://pmuynwsqmb.execute-api.ap-southeast-1.amazonaws.com/prod/getClients",
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    // Check for a successful response
    if (response.ok) {
      const data = await response.json();
      // Sort the clients by name alphabetically
      const sortedClients = data.sort((a, b) => a.name.localeCompare(b.name));
      return sortedClients; // Return the sorted list of clients
    } else {
      console.error(
        `Error: Failed to fetch clients. Status: ${response.status}`
      );
      return null;
    }
  } catch (error) {
    // Catch any network or other errors
    console.error(
      "Error: Network or other error occurred while fetching clients.",
      error
    );
    return null;
  }
}

export async function getClient(id) {
  try {
    // Make the API call using the provided client ID
    const response = await fetch(
      `https://9fd7dzyb4i.execute-api.ap-southeast-1.amazonaws.com/prod/getClient/${id}`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    // Check for a successful response
    if (response.ok) {
      const data = await response.json();
      return data; // Return the client data
    } else {
      console.error(
        `Error: Failed to fetch client. Status: ${response.status}`
      );
      return null;
    }
  } catch (error) {
    // Catch any network or other errors
    console.error(
      "Error: Network or other error occurred while fetching the client.",
      error
    );
    return null;
  }
}

export async function putClient(clientId, clientParameters = {}) {
  // Trim the client name if it exists to prevent trailing spaces
  const cleanedParameters = {
    ...clientParameters,
    name: clientParameters.name ? clientParameters.name.trim() : clientParameters.name
  };

  // Construct the payload with spread syntax
  const clientPayload = {
    client_data: [
      {
        ...cleanedParameters, // Spread all properties from cleanedParameters
        user_create: cleanedParameters.user_create || "system_default", // Default value
        user_update: cleanedParameters.user_update || "system_default", // Default value
      },
    ],
  };

  try {
    // Make the API call with PUT method
    const response = await fetch(
      `https://0t42ce2wea.execute-api.ap-southeast-1.amazonaws.com/prod/putClient/${clientId}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(clientPayload),
      }
    );

    // Check for a successful response
    if (response.ok) {
      return await response.json(); // Return the response as JSON
    } else {
      console.error(
        `Error: Failed to update client data. Status: ${response.status}`
      );
      return null;
    }
  } catch (error) {
    // Catch any network or other errors
    console.error(
      "Error: Network or other error occurred while updating client data.",
      error
    );
    return null;
  }
}

export async function getCacheClients() {
  try {
    const response = await fetch("https://tqltj9izzf.execute-api.ap-southeast-1.amazonaws.com/prod/getCacheClients", {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    });

    if (!response.ok) {
      console.error(`Error: Failed to fetch cache clients. Status: ${response.status}`);
      return null;
    }

    const data = await response.json();

    // 1) trim names
    const cleaned = (Array.isArray(data) ? data : []).map(c => ({
      ...c,
      name: c?.name ? c.name.trim() : c?.name
    }));

    // 2) de-dupe by id (fallback to normalized name if id is missing)
    const byKey = new Map();
    for (const c of cleaned) {
      const key = c?.id ?? (c?.name || "").toLowerCase();
      if (!byKey.has(key)) byKey.set(key, c);
    }
    const unique = Array.from(byKey.values());

    // 3) sort
    unique.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
    return unique;
  } catch (error) {
    console.error("Error: Network or other error occurred while fetching cache clients.", error);
    return null;
  }
}

export async function putCacheClient(id, cacheClientParameters = {}) {
  // Prepare the data to be submitted, trimming the name if it exists
  const submitData = {
    ...cacheClientParameters, // Spread all the properties from cacheClientParameters
    name: cacheClientParameters.name ? cacheClientParameters.name.trim() : cacheClientParameters.name
  };

  try {
    // Make the API call
    const response = await fetch(
      `https://rrrl5l9kp6.execute-api.ap-southeast-1.amazonaws.com/prod/putCacheClient/${id}`,
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(submitData), // Send submitData directly
      }
    );

    console.log("API Response:", response);

    // Check for a successful response
    if (response.ok) {
      return await response.json(); // Parse and return the JSON response
    } else {
      console.error(
        `Error: Failed to update cache client data. Status: ${response.status}`
      );
      return null;
    }
  } catch (error) {
    // Catch any network or other errors
    console.error(
      "Error: Network or other error occurred while updating cache client data.",
      error
    );
    return null;
  }
}

export async function getJobOrdersByClientId(clientId) {
  try {
    const response = await fetch(
      `https://l4erthvkkb.execute-api.ap-southeast-1.amazonaws.com/prod/getJobOrdersByClientId/${clientId}`,
      {
        method: "GET",
        headers: { "Content-Type": "application/json" },
      }
    );

    if (!response.ok) {
      console.error(`Error: Failed to fetch job orders. Status: ${response.status}`);
      throw new Error(`Failed to fetch job orders. Status: ${response.status}`);
    }

    const data = await response.json();
    const orders = Array.isArray(data.job_orders) ? data.job_orders : [];
    const clientName =
      orders.length > 0 && orders[0]?.client?.name ? orders[0].client.name : "";

    return { orders, clientName };
  } catch (err) {
    console.error("Error: Network or other error occurred while fetching job orders.", err);
    throw err;
  }
}
